import {Decimal} from 'decimal.js';
import {DEX_NONE_VALUE} from './const';
import {Deliverable} from './types/deliverable';

export const enum DecimalRounding {
    UP         = 0,
    DOWN       = 1,
    CEIL       = 2,
    FLOOR      = 3,
    HALF_UP    = 4,
    HALF_DOWN  = 5,
    HALF_EVEN  = 6,
    HALF_CEIL  = 7,
    HALF_FLOOR = 8
}

export const adjustDp = (value: bigint, origDp: number, newDp: number): bigint =>
    origDp === newDp
        ? value
        : origDp < newDp
            ? value * BigInt(10 ** (newDp - origDp))
            : value / BigInt(10 ** (origDp - newDp));

export const mathWithDiffDp = (v1: bigint, dp1: number, v2: bigint, dp2: number, dpResult: number, math: (v1: bigint, v2: bigint) => bigint): bigint => {
    const maxDp = Math.max(dp1, dp2, dpResult);
    const mathResult = math(adjustDp(v1, dp1, maxDp), adjustDp(v2, dp2, maxDp));

    return adjustDp(mathResult, maxDp, dpResult);
};

export const compareWithDiffDp = (v1: bigint, dp1: number, v2: bigint, dp2: number): number => {
    const maxDp = Math.max(dp1, dp2);
    const mathResult = adjustDp(v1, dp1, maxDp) - adjustDp(v2, dp2, maxDp);

    return mathResult < 0 ? -1 : (mathResult > 0 ? 1 : 0);
};

export const bigintAbs = (n: bigint): bigint => n < 0n ? -n : n;

export const vToDec = (v: bigint | string, dp: number): Decimal =>
    new Decimal(`${String(v)}e-${dp}`);

const decToBigInt = (v: Decimal, dp: number, rounding?: Decimal.Rounding): bigint =>
    BigInt(v.toFixed(dp, rounding).replace('.', ''));

export const mathWithDiffDpDecimalJs = (
    v1: bigint,
    dp1: number,
    v2: bigint,
    dp2: number,
    dpResult: number,
    math: (v1: Decimal, v2: Decimal) => Decimal,
    rounding?: Decimal.Rounding
): bigint => {
    const a = vToDec(v1, dp1);
    const b = vToDec(v2, dp2);
    const c = math(a, b);

    return decToBigInt(c, dpResult, rounding);
};

export interface ValueWithDp {
    v: bigint | undefined;
    dp: number;
}

export interface ValueWithDel {
    v: bigint | undefined;
    d: Deliverable;
}

export interface ValueWithDelDp extends ValueWithDel {
    dp?: number; // Overrides decimal places
}

export interface ValueWithDelAndComments {
    v: bigint | undefined;
    d: Deliverable;
    mathComments?: string;
}

export const mathValueWithDpDecJs = (
    v1: ValueWithDel,
    v2: ValueWithDel,
    dpResult: number,
    math: (v1: Decimal, v2: Decimal) => Decimal,
    rounding?: Decimal.Rounding
): bigint =>
    mathWithDiffDpDecimalJs(v1.v, v1.d.decimalPlaces, v2.v, v2.d.decimalPlaces, dpResult, math, rounding);

export const mathValueWithDelDecJs = (
    v1: ValueWithDel,
    v2: ValueWithDel,
    delResult: Deliverable,
    math: (v1: Decimal, v2: Decimal) => Decimal,
    rounding?: Decimal.Rounding
): ValueWithDel => {
    const a = vToDec(v1.v, v1.d.decimalPlaces);
    const b = vToDec(v2.v, v2.d.decimalPlaces);
    const c = math(a, b);

    return {
        v: decToBigInt(c, delResult.decimalPlaces, rounding),
        d: delResult
    };
};

export const percentValueWithDel = (part: ValueWithDel, total: ValueWithDel): number => {
    return part.v && total.v
        ? (
            Number(
                mathValueWithDpDecJs(part, total, 2, (v1, v2) => v1.mul(100).div(v2))
            ) / 100
        )
        : 0;
};

export const mathValueWithDel = (v1: ValueWithDel, v2: ValueWithDel, delResult: Deliverable, math: (v1: bigint, v2: bigint) => bigint): ValueWithDel => {
    return {
        v: mathWithDiffDp(
            v1.v,
            v1.d.decimalPlaces,
            v2.v,
            v2.d.decimalPlaces,
            delResult.decimalPlaces,
            math
        ),
        d: delResult
    };
};

export const numToValueWithDel = (numValue: Decimal.Value, d: Deliverable): ValueWithDel => {
    const decValue = new Decimal(numValue);
    const v = decValue.isFinite()
        ? decToBigInt(decValue, d.decimalPlaces)
        : undefined;

    return {v, d};
};

export const valueWithDelToDec = (v: ValueWithDel): Decimal =>
    vToDec(v.v, v.d.decimalPlaces);

export const numToV = (numValue: Decimal.Value, dp: number, rounding?: DecimalRounding): bigint => {
    const decValue = new Decimal(numValue);

    return decValue.isFinite()
        ? decToBigInt(decValue, dp, rounding)
        : undefined;
};

export const parseDexBigIntStr = (str: string): bigint | undefined => {
    let v: bigint | undefined;

    if (str) {
        v = BigInt(str);

        if (v === DEX_NONE_VALUE) {
            v = undefined;
        }
    }

    return v;
};
